import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SignalProvider } from './signal-provider.interface';
import {
  WidgetSignalConfig,
  WidgetRegistration,
  WidgetRefreshState,
  SignalChange,
} from './signal.types';

/**
 * The "brain". Routes incoming signals to registered widgets and manages:
 * debounce per widget, in-flight tracking with a trailing recheck, a
 * concurrency-limited priority queue, page-visibility gating, and stale
 * detection on mount.
 *
 * Lifecycle:
 *   initialize(familyRootId)  -> on login
 *   onOfficeSwitch            -> teardown() + initialize(newFamilyRootId)
 *   teardown()                -> on logout
 *
 * NOTE: not registered/used until Phase 0 spike passes and widgets are wired.
 */
@Injectable({ providedIn: 'root' })
export class SignalCoordinatorService implements OnDestroy {
  private widgets = new Map<string, WidgetRefreshState>();
  private signalSub: Subscription | null = null;
  private connectedScopes = new Set<string>();

  private isPageVisible = true;
  /** widgetId -> signalTimestamp received while hidden/unmounted. */
  private deferredSignals = new Map<string, number>();

  private activeRequests = 0;
  private readonly MAX_CONCURRENT = 3;
  private requestQueue: Array<{ widgetId: string; priority: number }> = [];

  /** Family root (parent office) this session is subscribed to. */
  private familyRootId: number | null = null;

  constructor(private signalProvider: SignalProvider) {
    document.addEventListener('visibilitychange', () => {
      this.isPageVisible = !document.hidden;
      if (this.isPageVisible) {
        this.processDeferredSignals();
      }
    });
  }

  // ── Connection management ──────────────────────────────────────────────

  /**
   * The office family root this session is listening on, or null before login.
   * Exposed so features that watch a payload node directly (drawing progress) can
   * resolve the same node path without duplicating the login plumbing.
   */
  get familyRoot(): number | null {
    return this.familyRootId;
  }

  /** Call once after login. familyRootId comes from the login response. */
  initialize(familyRootId: number): void {
    this.familyRootId = familyRootId;
    this.connectToScope('global');

    if (!this.signalSub) {
      this.signalSub = this.signalProvider.changes$.subscribe((change) =>
        this.handleSignalChange(change)
      );
    }
  }

  /** Connect to a warehouse-specific scope (on entering a warehouse view). */
  connectWarehouse(warehouseId: number): void {
    this.connectToScope(`warehouse_${warehouseId}`);
  }

  /** Disconnect a warehouse scope (on leaving the view). */
  disconnectWarehouse(warehouseId: number): void {
    const scope = `warehouse_${warehouseId}`;
    this.signalProvider.disconnect(scope);
    this.connectedScopes.delete(scope);
  }

  /** Tear everything down. Call on logout AND before re-initializing on office switch. */
  teardown(): void {
    this.signalProvider.disconnectAll();
    this.connectedScopes.clear();
    this.widgets.forEach((w) => {
      if (w.debounceTimer) clearTimeout(w.debounceTimer);
      if (w.inFlightSub) w.inFlightSub.unsubscribe();
    });
    this.widgets.clear();
    this.deferredSignals.clear();
    this.requestQueue = [];
    this.activeRequests = 0;
    this.familyRootId = null;
    if (this.signalSub) {
      this.signalSub.unsubscribe();
      this.signalSub = null;
    }
  }

  // ── Widget registration ────────────────────────────────────────────────

  register(config: WidgetSignalConfig): WidgetRegistration {
    const state: WidgetRefreshState = {
      config,
      status: 'idle',
      lastFetchedAt: 0,
      lastSignalAt: 0,
      pendingSignalAt: 0,
      debounceTimer: null,
      inFlightSub: null,
      isMounted: true,
    };

    this.widgets.set(config.id, state);

    // If a signal arrived while this widget was on another page, refresh now.
    const storedSignal = this.deferredSignals.get(config.id);
    if (storedSignal && storedSignal > state.lastFetchedAt) {
      this.scheduleRefresh(config.id, storedSignal);
      this.deferredSignals.delete(config.id);
    }

    return { id: config.id, unregister: () => this.unregister(config.id) };
  }

  /** Force-refresh one widget (e.g. a Refresh button). */
  manualRefresh(widgetId: string): void {
    if (this.widgets.get(widgetId)) {
      this.executeRefresh(widgetId);
    }
  }

  /** Force-refresh all mounted widgets (e.g. a global Refresh button). */
  refreshAll(): void {
    this.widgets.forEach((state, id) => {
      if (state.isMounted) this.executeRefresh(id);
    });
  }

  // ── Signal handling ────────────────────────────────────────────────────

  private connectToScope(scope: string): void {
    if (this.familyRootId && !this.connectedScopes.has(scope)) {
      this.signalProvider.connect(this.familyRootId, scope);
      this.connectedScopes.add(scope);
    }
  }

  private handleSignalChange(change: SignalChange): void {
    const changedKeys = Object.keys(change.changed);

    this.widgets.forEach((state, widgetId) => {
      if (!state.isMounted) return;
      if (state.config.scope !== change.scope) return;

      const isAffected = state.config.signals.some((sig) =>
        changedKeys.includes(sig)
      );
      if (!isAffected) return;

      const relevantTimestamp = Math.max(
        ...state.config.signals
          .filter((sig) => change.changed[sig] !== undefined)
          .map((sig) => change.changed[sig])
      );

      if (!this.isPageVisible) {
        this.deferredSignals.set(widgetId, relevantTimestamp);
        return;
      }

      this.scheduleRefresh(widgetId, relevantTimestamp);
    });
  }

  private scheduleRefresh(widgetId: string, signalTimestamp: number): void {
    const state = this.widgets.get(widgetId);
    if (!state) return;

    state.lastSignalAt = signalTimestamp;

    // Already fetching — record as pending; the trailing check handles it.
    if (state.status === 'fetching') {
      state.pendingSignalAt = signalTimestamp;
      return;
    }

    if (state.debounceTimer) clearTimeout(state.debounceTimer);

    state.status = 'debouncing';
    const debounceMs = state.config.debounceMs ?? 500;
    state.debounceTimer = setTimeout(() => {
      state.debounceTimer = null;
      this.enqueueRefresh(widgetId);
    }, debounceMs);
  }

  // ── Request queue & concurrency ────────────────────────────────────────

  private enqueueRefresh(widgetId: string): void {
    const state = this.widgets.get(widgetId);
    if (!state) return;

    if (this.activeRequests < this.MAX_CONCURRENT) {
      this.executeRefresh(widgetId);
    } else {
      this.requestQueue.push({ widgetId, priority: state.config.priority });
      this.requestQueue.sort((a, b) => a.priority - b.priority);
    }
  }

  private executeRefresh(widgetId: string): void {
    const state = this.widgets.get(widgetId);
    if (!state || !state.isMounted) return;

    if (state.inFlightSub) state.inFlightSub.unsubscribe();

    state.status = 'fetching';
    state.pendingSignalAt = 0;
    const fetchStartedAt = Date.now();
    this.activeRequests++;

    state.inFlightSub = state.config
      .fetchFn()
      .pipe(
        finalize(() => {
          this.activeRequests--;
          state.inFlightSub = null;
          state.lastFetchedAt = fetchStartedAt;

          // Trailing check: did a newer signal arrive mid-fetch?
          if (state.pendingSignalAt > 0) {
            state.status = 'trailing-check';
            state.pendingSignalAt = 0;
            setTimeout(() => {
              if (state.isMounted) this.executeRefresh(widgetId);
            }, 300);
          } else {
            state.status = 'idle';
          }

          this.dequeueNext();
        })
      )
      .subscribe({
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(`[SignalCoordinator] Fetch failed for ${widgetId}:`, err);
          state.status = 'idle';
          // The widget component renders its own error UI.
        },
      });
  }

  private dequeueNext(): void {
    if (
      this.requestQueue.length > 0 &&
      this.activeRequests < this.MAX_CONCURRENT
    ) {
      const next = this.requestQueue.shift();
      if (next) this.executeRefresh(next.widgetId);
    }
  }

  // ── Visibility & deferred processing ───────────────────────────────────

  private processDeferredSignals(): void {
    if (this.deferredSignals.size === 0) return;

    let delay = 0;
    const STAGGER_MS = 100;

    this.deferredSignals.forEach((signalTimestamp, widgetId) => {
      const state = this.widgets.get(widgetId);
      if (state && state.isMounted && signalTimestamp > state.lastFetchedAt) {
        setTimeout(() => this.scheduleRefresh(widgetId, signalTimestamp), delay);
        delay += STAGGER_MS;
      }
    });

    this.deferredSignals.clear();
  }

  // ── Cleanup ────────────────────────────────────────────────────────────

  private unregister(widgetId: string): void {
    const state = this.widgets.get(widgetId);
    if (state) {
      state.isMounted = false;
      if (state.debounceTimer) clearTimeout(state.debounceTimer);
      if (state.inFlightSub) state.inFlightSub.unsubscribe();
      this.widgets.delete(widgetId);
    }
    this.requestQueue = this.requestQueue.filter((r) => r.widgetId !== widgetId);
  }

  ngOnDestroy(): void {
    this.teardown();
  }
}
